import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ChatIconOutline, ChatMessageDisplay, CheckMarkIcon, CloseIcon, InfoIcon, LeftCaretIcon, ListHeader, RowsContainer, ScrollableContainer, SpeakerItem, UpArrowIcon, } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import cx from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';
import { useKeyStreak } from '../../hooks/useKeyStreak';
import { UserService } from '../../services/user/UserService';
import { AcceptedParticipantType, UserRole, } from '../../services/user/UserServiceOutput';
import { Popup, TopLeft } from '../../utils/popups';
import { TextStyle } from '../../utils/styled';
import { AbstractPopup, DismissalReason, } from './AbstractPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { PopupCss, PopupCssPreset } from './Popup.styles';
export function SpeakersPopup({ reject, variant, ...props }) {
    const [activePerson, setActivePerson] = useState(null); // @todo Control with real state.
    useKeyStreak('Escape', () => {
        if (activePerson) {
            setActivePerson(null);
        }
    });
    return (_jsx(SpeakersPopupRoot, { reject: (reason) => {
            if (reason === DismissalReason.Escape && activePerson) {
                return;
            }
            reject(reason);
        }, translateFn: variant === 'desktop' ? TopLeft : undefined, ...props, className: cx(props.className, variant), children: _jsx(ScrollablePopupContainer, { fullscreenOnly: variant === 'mobile', onCloseButtonClick: () => {
                reject(DismissalReason.CloseButton);
            }, children: _jsx(SpeakersPopupContent, { children: !activePerson ? (_jsx(PeopleView, { reject: reject, onChatButtonClick: (person) => {
                        setActivePerson(person);
                    } })) : (_jsx(ChatView, { person: activePerson, onBackButtonClick: () => {
                        setActivePerson(null);
                    }, onCloseButtonClick: () => {
                        reject(DismissalReason.CloseButton);
                    } })) }) }) }));
}
const SpeakersPopupContent = styled(RowsContainer) `
    width: 100%;
    gap: 1rem;
    padding: 0.25rem;
    box-sizing: border-box;
`;
const SpeakersPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    &.desktop {
        @media (min-width: 30rem) {
            /* Use frosted glass, bound, and slide in from above styles */
            ${PopupCss.FrostedGlass};
            ${PopupCss.NoBorder};
            ${PopupCss.NoOffsetX};
            ${PopupCss.SlideInAbove};
            ${PopupCss.Bound};
            
            --Popup_BorderRadius: 1rem;
            --Popup_Width: 25rem;
            --Popup_Padding: 1rem 0;
            --Popup_Height: auto;
            --Popup_OffsetTransition: initial;
            
            --Popup_Left: 0;
            --Popup_Top: 0;
            
            /* Position horizontally at the anchor's left edge */
            --Popup_X: min(
                var(--Popup_MaxX),
                max(var(--Popup_MinXY), var(--Popup_AnchorX, 0rem))
            );
            
            /* Position vertically above the anchor */
            --Popup_Y: min(
                var(--Popup_MaxY),
                max(
                    var(--Popup_MinXY),
                    calc(
                        var(--Popup_AnchorY, 0rem) - 100% -
                            var(--Popup_AnchorOffset, 0.5rem)
                    )
                )
            );
            
            /* Calculate max height to fit above the anchor */
            max-height: calc(var(--Popup_AnchorY, 0px) - 2rem);
        }
    }

    &.mobile {
        --SettingsContainer_InnerMaxWidth: 25rem;
    }
`;
const speakersPopup = Popup.create(SpeakersPopup);
export function showSpeakersPopup({ anchorRef, variant, onBeforeOpen, onDismiss, }) {
    executeSoon(async () => {
        onBeforeOpen?.();
        try {
            await speakersPopup.pop({
                anchorRef,
                variant,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    });
}
const SpeakersPopupHeader = styled.div `
    ${TextStyle.Heading_3};
    display: flex;
    align-items: center;
    gap: 0.75rem;
    position: relative;
    z-index: 1;
`;
const SpeakerPopupTitle = styled.div `
    font-weight: 600;
    flex-grow: 1;
`;
const Separator = styled.div `
    height: 1px;
    background-color: hsla(0, 0%, 100%, 0.1);
`;
const ListWrap = styled.div ``;
const ListItems = styled.div `
    & > div {
        margin-top: 0.5rem;
    }
`;
const HeaderButton = styled(Button) `
    --Button_Height: 1.75rem;
    --Button_Width: 1.75rem;

    svg {
        transform: scale(0.75);
    }
`;
const SpeakerActionGlossyButton = styled(Button) `
    --Button_Background: rgba(47, 47, 47, 0.6);
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    --tv-ui-shiny-border-thickness: 0.0625rem;

    svg {
        transform: scale(0.85);
    }
`;
const SpeakerActionButton = styled(Button) `
    --Button_Background: rgba(255, 255, 255, 0);
    --Button_HoverBackground: rgba(255, 255, 255, 0.05);
    --Button_Width: 2rem;
    --Button_Height: 2rem;

    svg {
        transform: scale(0.85);
    }
`;
const Lists = styled.div `
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`;
const UnreadMessageIndicator = styled.div `
    width: 0;
    height: 0;
    overflow: visible;
    position: absolute;

    &::after {
        content: '';
        display: block;
        width: 0.375rem;
        height: 0.375rem;
        border-radius: 50%;
        background-color: #FF4015;
        position: absolute;
        transform: translate(-50%, -50%) translate(0.35rem, -0.35rem);
    }
`;
function PeopleView({ reject, onChatButtonClick }) {
    const incomingRequests = UserService.getOutput().useIncomingRequests();
    const acceptedParticipantType = UserService.getOutput().useAcceptedParticipantType();
    const isCallMode = acceptedParticipantType === AcceptedParticipantType.SPEAKERS;
    const speakerRequests = incomingRequests.filter((request) => request.userRole === UserRole.SPEAKER);
    const guestRequests = incomingRequests.filter((request) => request.userRole === UserRole.GUEST);
    const speakers = UserService.getOutput().useSpeakers();
    const guests = UserService.getOutput().useGuests();
    const displayNames = UserService.getOutput().useDisplayNames();
    const permissionService = UserService.getNotificationInterface();
    const resolveName = (userId) => displayNames[userId] || userId;
    const hasUnread = true;
    const participantActions = (userId, status) => (_jsxs(_Fragment, { children: [_jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    permissionService.notifyRevokePermission(userId, status);
                }, children: _jsx(CloseIcon, {}) }), !isCallMode && (_jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    onChatButtonClick?.({
                        id: userId,
                        name: resolveName(userId),
                    });
                }, children: _jsx(ChatIconOutline, {}) }))] }));
    const requestActions = (request) => (_jsxs(_Fragment, { children: [_jsx(SpeakerActionGlossyButton, { kind: "pill", className: "tv-ui-shiny-border", onClick: () => {
                    permissionService.notifyAcceptPermission(request.userId, request.userRole);
                }, children: _jsx(CheckMarkIcon, {}) }), _jsx(SpeakerActionGlossyButton, { kind: "pill", className: "tv-ui-shiny-border", onClick: () => {
                    permissionService.notifyRejectPermission(request.userId, request.userRole);
                }, children: _jsx(CloseIcon, {}) }), !isCallMode && (_jsxs(SpeakerActionGlossyButton, { kind: "pill", className: "tv-ui-shiny-border", onClick: () => {
                    onChatButtonClick?.({
                        id: request.userId,
                        name: resolveName(request.userId),
                    });
                }, children: [_jsx(ChatIconOutline, {}), hasUnread ? _jsx(UnreadMessageIndicator, {}) : null] }))] }));
    return (_jsxs(_Fragment, { children: [_jsxs(SpeakersPopupHeader, { children: [_jsx(SpeakerPopupTitle, { children: "People" }), _jsx(HeaderButton, { kind: "pill", onClick: () => {
                            reject(DismissalReason.CloseButton);
                        }, children: _jsx(CloseIcon, {}) })] }), _jsxs(Lists, { children: [guestRequests.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Guest requests", count: guestRequests.length }), _jsx(ListItems, { children: guestRequests.map((request) => (_jsx(SpeakerItem, { name: resolveName(request.userId), actions: requestActions(request) }, request.userId))) })] }), _jsx(Separator, {})] })), speakerRequests.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Speaker requests", count: speakerRequests.length }), _jsx(ListItems, { children: speakerRequests.map((request) => (_jsx(SpeakerItem, { name: resolveName(request.userId), actions: requestActions(request) }, request.userId))) })] }), _jsx(Separator, {})] })), guests.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Guests", count: guests.length }), _jsx(ListItems, { children: guests.map((guestId) => (_jsx(SpeakerItem, { name: resolveName(guestId), actions: participantActions(guestId, UserRole.GUEST) }, guestId))) })] })), speakers.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Speakers", count: speakers.length }), _jsx(ListItems, { children: speakers.map((speakerId) => (_jsx(SpeakerItem, { name: resolveName(speakerId), actions: participantActions(speakerId, UserRole.SPEAKER) }, speakerId))) })] }))] })] }));
}
const ChatView = ({ person, onCloseButtonClick, onBackButtonClick, }) => {
    const [messageText, setMessageText] = useState('');
    // Get chat messages for this person
    const messages = UserService.getOutput().usePrivateChatMessagesFromUser(person.id);
    const permissionService = UserService.getNotificationInterface();
    const handleSendMessage = () => {
        if (messageText.trim()) {
            permissionService.notifySendPrivateMessage(person.id, messageText);
            setMessageText('');
        }
    };
    return (_jsxs(ChatViewRoot, { children: [_jsxs(SpeakersPopupHeader, { children: [onBackButtonClick && (_jsx(HeaderButton, { kind: "pill", onClick: () => {
                            onBackButtonClick();
                        }, children: _jsx(LeftCaretIcon, {}) })), _jsx(SpeakerPopupTitle, { children: person.name }), _jsxs(InfoButtonWrap, { children: [_jsx(HeaderButton, { kind: "pill", children: _jsx(InfoIcon, {}) }), _jsx(PrivacyNote, { className: "tv-ui-shiny-border", children: "Only you and the receptor can see this chat. It will not be shown on live stream." })] }), onCloseButtonClick && (_jsx(HeaderButton, { kind: "pill", onClick: () => {
                            onCloseButtonClick();
                        }, children: _jsx(CloseIcon, {}) }))] }), _jsx(ScrollableContainer, { children: _jsx(ChatMessages, { children: messages.length === 0 ? (_jsx(EmptyChat, { children: "No messages yet. Start a conversation!" })) : (messages.map((msg, index) => (_jsx(ChatMessageDisplay, { content: msg.message, authorNickname: msg.isSent ? 'You' : person.name }, `${msg.timestamp}-${index}`)))) }) }), _jsxs(ChatMessageTextAreaWrap, { children: [_jsx(ChatMessageTextArea, { placeholder: "Send a message", name: "msg", value: messageText, onChange: (e) => setMessageText(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        } }), _jsx(SubmitChatMessageButton, { kind: "pill", onClick: handleSendMessage, disabled: !messageText.trim(), children: _jsx(UpArrowIcon, {}) })] })] }));
};
const ChatMessages = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
`;
const EmptyChat = styled.div `
    ${TextStyle.Body_M};
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
    padding: 2rem;
`;
const ChatMessageTextArea = styled.textarea `
    width: 100%;
    height: 8rem;
    background-color: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 0.5rem;
    padding: 1rem;
    box-sizing: border-box;
    color: white;
    font-family: inherit;
    resize: none;
    display: block;

    &:focus {
        outline: none;
        background-color: rgba(0, 0, 0, 0.35);
    }
`;
const ChatViewRoot = styled.div `
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: calc(var(--Popup_Height) - 2.5rem);
    gap: 1.5rem;
`;
const ChatMessageTextAreaWrap = styled.div `
    position: relative;
`;
const SubmitChatMessageButton = styled(SpeakerActionButton) `
    --Button_TextColor: black;
    --Button_Background: white;
    --Button_HoverBackground: white;
    position: absolute;
    right: 1rem;
    bottom: 1rem;
`;
const PrivacyNote = styled.div `
    --tv-ui-shiny-border-radius: 0.75rem;
    --tv-ui-shiny-border-thickness: 0.0625rem;
    ${TextStyle.Body_M};
    position: absolute;
    width: 18rem;
    background-color: #3E3E3E;
    right: 0;
    top: 2.25rem;
    padding: 0.25rem 0.75rem;
`;
const InfoButtonWrap = styled.div `
    position: relative;

    &:hover ${PrivacyNote} {
        display: block;
    }

    ${PrivacyNote} {
        display: none;
    }
`;
